import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  input,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatDivider } from '@angular/material/divider';
import { MatIcon } from '@angular/material/icon';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { NavigationEnd, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { NotificationId, UserApplications, UserDto } from '@workspace/data-types';
import {
  ApplicationStore,
  NavigationItem,
  NavigationKeys,
  NavigationStore,
  NotificationStateService,
  PublicAudience,
  Scheme,
} from '@workspace/ng-data-access';
import { filter, map, startWith, tap } from 'rxjs';
import { Theming } from '../../../../providers';
import { ProfileAppSwitcherComponent } from '../../../buttons';
import { AuraImpersonateBannerComponent, SidebarUserAuraComponent } from '../../../layouts';
import { AuraDiscussionsButtonComponent } from '../../../notifications/discussions-button.component';
import { AuraNotificationsDropdownComponent } from '../../../notifications/notifications-dropdown.component';

export interface StackedLayoutAudienceOption {
  audience: PublicAudience;
  label: string;
  shortLabel?: string;
  icon: string;
  targetRoute: string;
  badge?: string;
  ctaLabel: string;
  ctaIcon: string;
  ctaRouterLink?: string;
  ctaHref?: string;
}

@Component({
  selector: 'aura-stacked-layout-simple',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatIcon,
    MatIconButton,
    MatButton,
    RouterLink,
    RouterLinkActive,
    MatMenu,
    MatMenuTrigger,
    MatMenuItem,
    RouterOutlet,
    MatDivider,
    SidebarUserAuraComponent,
    AuraImpersonateBannerComponent,
    AuraNotificationsDropdownComponent,
    AuraDiscussionsButtonComponent,
    ProfileAppSwitcherComponent,
  ],
  templateUrl: './stacked-layout-simple.component.html',
})
export class StackedLayoutSimple {
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly appStore = inject(ApplicationStore);
  private readonly navigationStore = inject(NavigationStore);
  protected readonly theming = inject(Theming);
  protected readonly notificationState = inject(NotificationStateService);

  private readonly contentContainer = viewChild<ElementRef<HTMLElement>>('contentContainer');

  readonly user = input.required<UserDto | null>();
  readonly navigationItems = input.required<NavigationItem[]>();
  readonly appName = input<string, string | undefined | null>('', {
    transform: (v) => v || '',
  });
  readonly logoUrl = input<string, string | undefined | null>('/images/logo/logo.svg', {
    transform: (v) => v || '/images/logo/logo.svg',
  });
  readonly homeUrl = input<string, string | undefined | null>('/', {
    transform: (v) => v || '/',
  });
  readonly appBadge = input<string | null, string | undefined | null>(null, {
    transform: (v) => v ?? null,
  });
  readonly audienceOptions = input<
    StackedLayoutAudienceOption[],
    StackedLayoutAudienceOption[] | undefined | null
  >([], {
    transform: (v) => v ?? [],
  });

  protected readonly isPublicApp = computed(
    () => this.navigationStore.currentKey() === NavigationKeys.PUBLIC,
  );
  protected readonly isAgentApp = computed(
    () => this.appStore.currentApplicationName() === UserApplications.AGENT,
  );
  protected readonly publicAudience = this.navigationStore.currentPublicAudience;
  protected readonly activeAudienceOption = computed<StackedLayoutAudienceOption | null>(() => {
    const options = this.audienceOptions();
    if (!options.length) return null;
    return options.find((opt) => opt.audience === this.publicAudience()) ?? options[0];
  });
  protected readonly effectiveBadge = computed(
    () => this.appBadge() || this.activeAudienceOption()?.badge || (this.isAgentApp() ? 'Pro' : null),
  );
  protected readonly effectiveHomeUrl = computed(
    () => this.activeAudienceOption()?.targetRoute || this.homeUrl(),
  );

  private readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map((e) => e.urlAfterRedirects.split('?')[0].split('#')[0]),
      startWith(this.router.url.split('?')[0].split('#')[0]),
    ),
    { initialValue: this.router.url.split('?')[0].split('#')[0] },
  );

  protected readonly isHomePage = computed(() => {
    const current = this.currentUrl();
    const target = this.effectiveHomeUrl();
    if (target === '/') {
      return current === '/' || current === '' || current === '/home';
    }
    return current === target;
  });

  protected readonly notifications = this.notificationState.notifications;
  protected readonly unreadCount = this.notificationState.unreadCount;
  protected readonly scheme = computed(() => this.theming.scheme());

  constructor() {
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        tap(() => {
          this.contentContainer()?.nativeElement.scrollTo({ top: 0 });
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  protected switchPublicAudience(option: StackedLayoutAudienceOption): void {
    this.navigationStore.setPublicAudience(option.audience);
    void this.router.navigate([option.targetRoute]);
  }

  protected updateScheme(scheme: Scheme): void {
    this.theming.scheme.update(() => scheme);
  }

  protected markAsRead(id: NotificationId): void {
    this.notificationState.markAsRead(id);
  }

  protected markAllAsRead(): void {
    this.notificationState.markAllAsRead();
  }

  protected goToNotifications(): void {
    void this.router.navigate(['/user/notifications']);
  }
}
